import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import BodyShape from '@/components/common/svg/shapes/BodyShape';
import RulerShape from '@/components/common/svg/shapes/RulerShape';
import {
  AlcoholPreference,
  BodyType,
  SmokingStatus,
  ZodiacSign
} from '@/enums/apiEnums.ts';
import { getBodyTypes } from '@/components/Popup/EditProfile/EditBodyTypePopup.tsx';
import { getSmokingOptions } from '@/components/Popup/EditProfile/EditSmokingStatusPopup.tsx';
import SmokingShape from '@/components/common/svg/shapes/SmokingShape.tsx';
import { getAlcoholPreferences } from '@/components/Popup/EditProfile/EditAlcoholPreferencePopup.tsx';
import AlcoholShape from '@/components/common/svg/shapes/AlcoholShape.tsx';
import { CandidateUI } from '@/api/matchmaker/types.ts';
import {
  getZodiacSigns,
  zodiacIcons
} from '@/components/Popup/EditProfile/EditZodiacPopup.tsx';

const LabelStack = ({
  matchCandidate,
  showOnly,
  maxLabels,
  showExpandButton = false,
  totalLabels
}: {
  matchCandidate: CandidateUI;
  showOnly?: string[];
  maxLabels?: number;
  showExpandButton?: boolean;
  totalLabels?: number;
}) => {
  const { t } = useTranslation();
  const { bodyType, heightCm, smokingStatus, alcoholPreference, zodiacSign } =
    matchCandidate.profile;

  const bodyTypes = getBodyTypes(t);
  const smokingOptions = getSmokingOptions(t);
  const alcoholPreferences = getAlcoholPreferences(t);
  const zodiacSigns = getZodiacSigns(t);
  const ZodiacSignShape = zodiacIcons[zodiacSign];

  const labels: { key: string; value: string; icon: JSX.Element }[] = [];

  if ((!showOnly || showOnly.includes('heightCm')) && heightCm > 0) {
    labels.push({
      key: 'heightCm',
      value: heightCm + ' ' + t('height_cm'),
      icon: (
        <RulerShape
          width={14}
          height={14}
          fillColor="#000000"
          style={{ marginRight: '2px' }}
        />
      )
    });
  }

  if (
    (!showOnly || showOnly.includes('zodiacSign')) &&
    zodiacSign !== ZodiacSign.ZODIAC_SIGN_UNSPECIFIED &&
    ZodiacSignShape
  ) {
    labels.push({
      key: 'zodiacSign',
      value: zodiacSigns[zodiacSign],
      icon: (
        <ZodiacSignShape
          width={14}
          height={14}
          fillColor="#000000"
          style={{ marginRight: '4px' }}
        />
      )
    });
  }

  if (
    (!showOnly || showOnly.includes('bodyType')) &&
    bodyType !== BodyType.BODY_TYPE_UNSPECIFIED
  ) {
    labels.push({
      key: 'bodyType',
      value: bodyTypes[bodyType],
      icon: (
        <BodyShape
          width={14}
          height={14}
          fillColor="#000000"
          style={{ marginRight: '2px' }}
        />
      )
    });
  }

  if (
    (!showOnly || showOnly.includes('smokingStatus')) &&
    smokingStatus !== SmokingStatus.SMOKING_STATUS_UNSPECIFIED
  ) {
    labels.push({
      key: 'smokingStatus',
      value: smokingOptions[smokingStatus],
      icon: (
        <SmokingShape
          width={14}
          height={14}
          fillColor="#000000"
          style={{ marginRight: '4px' }}
        />
      )
    });
  }

  if (
    (!showOnly || showOnly.includes('alcoholPreference')) &&
    alcoholPreference !== AlcoholPreference.ALCOHOL_PREFERENCE_UNSPECIFIED
  ) {
    labels.push({
      key: 'alcoholPreference',
      value: alcoholPreferences[alcoholPreference],
      icon: (
        <AlcoholShape
          width={14}
          height={14}
          fillColor="#000000"
          style={{ marginRight: '4px' }}
        />
      )
    });
  }

  if (labels.length === 0) return null;

  const labelsToShow = maxLabels ? labels.slice(0, maxLabels) : labels;
  const remainingLabelsCount = totalLabels
    ? totalLabels - labelsToShow.length
    : 0;

  return (
    <div className="cardLabelStack" style={containerStyle}>
      {labelsToShow.map((label) => (
        <span key={label.key} className="cardLabelItem" style={labelStyle}>
          {label.icon}
          {label.value}
        </span>
      ))}
      {showExpandButton && remainingLabelsCount > 0 && (
        <span className="cardLabelItem" style={expandButtonStyle}>
          +{remainingLabelsCount}
        </span>
      )}
    </div>
  );
};

const containerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  color: '#000000',
  textAlign: 'left',
  alignItems: 'center',
  gap: '6px',
  overflow: 'hidden',
  marginTop: '8px'
};

const labelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: '#ffffff',
  borderRadius: '15px',
  padding: '2px 10px 2px 8px',
  height: '26px',
  whiteSpace: 'nowrap',
  fontSize: '12px',
  minWidth: 0
};

const expandButtonStyle: CSSProperties = {
  ...labelStyle,
  cursor: 'pointer',
  fontWeight: '600'
};

export default LabelStack;
